import { registerPlugin, WebPlugin } from '@capacitor/core';

import { desktopBruecke, istDesktop } from './gleis';

/**
 * Abgleich der Geräteuhr gegen die Netzzeit.
 *
 * Nativ dahinter: `ios/App/App/ClockCheckPlugin.swift` (SNTP, RFC 4330), am Desktop
 * `desktop/zeit.cts` im Hauptprozess.
 *
 * ⚠️ **Ein Hinweis, kein Beweis** — SNTP ist unsigniert. Es fängt den plausiblen Fall ab:
 * ein Gerät, dessen Uhr schlicht daneben liegt. Gegen einen Angreifer hilft nur die
 * Empfangszeit des Servers oder ein Zeitstempeldienst (s. `docs/beglaubigung.md`).
 */

export interface ClockCheckPlugin {
  offsetSeconds(options?: {
    server?: string;
    timeoutMs?: number;
  }): Promise<{ available: boolean; offsetSeconds?: number }>;
}

/**
 * Im Browser wird nicht abgeglichen — und dann gibt es auch keine Meldung.
 *
 * ⛔ **Das bleibt so.** Eine Seite kommt an UDP 123 nicht heran, und jede Uhrzeit, die sie
 * über HTTP holte, wäre um die Laufzeit ungewiss — ein Versatz von einer Sekunde liesse sich
 * von einem Netzweg nicht unterscheiden. Lieber keine Auskunft als eine erfundene.
 */
class ClockCheckWeb extends WebPlugin implements ClockCheckPlugin {
  async offsetSeconds(): Promise<{ available: boolean }> {
    return { available: false };
  }
}

/**
 * Am Desktop fragt der Hauptprozess — `desktop/zeit.cts`, SNTP über UDP 123.
 *
 * ⚠️ **Auch hier wird nicht geworfen.** Fehlt die Brücke oder scheitert der Abgleich, kommt
 * `{ available: false }` zurück; dann bleibt es bei `timeSource: 'system'`, und der Prüfer
 * sieht nichts. Eine Meldung „Zeit nicht prüfbar" wäre in einem Prüfungsnetz der Normalfall
 * und damit nach zwei Tagen unsichtbar.
 */
export class ClockCheckDesktop extends WebPlugin implements ClockCheckPlugin {
  async offsetSeconds(
    options?: { server?: string; timeoutMs?: number },
  ): Promise<{ available: boolean; offsetSeconds?: number }> {
    const bruecke = desktopBruecke();
    const server = options?.server?.trim() ?? '';
    // Ohne Server ist nichts zu fragen: leer heisst, die Einrichtung will keinen Abgleich.
    if (!bruecke || !server) return { available: false };
    try {
      return await bruecke.zeit(server, options?.timeoutMs);
    } catch {
      return { available: false };
    }
  }
}

/**
 * ⚠️ **Die Wahl fällt bei jedem Aufruf, nicht beim Laden** — wie in `platform/screenshot.ts`:
 * `registerPlugin` ruft diese Fabrik erst, wenn das Plugin zum ersten Mal gebraucht wird.
 * Stünde die Entscheidung im Modulkopf, hinge sie davon ab, ob die Brücke zu diesem Zeitpunkt
 * schon dastand (s. `platform/gleis.ts`).
 */
export const ClockCheck = registerPlugin<ClockCheckPlugin>('ClockCheck', {
  web: () => (istDesktop() ? new ClockCheckDesktop() : new ClockCheckWeb()),
});

/**
 * Ab welcher Abweichung gewarnt wird.
 *
 * Zwei Minuten: darunter liegt jede Uhr, die einfach ein wenig driftet, und eine Meldung
 * dafür wäre Lärm. Darüber stimmt etwas nicht — eine verstellte Zeitzone macht eine ganze
 * Stunde, eine falsch gestellte Uhr meist mehr.
 */
export const WARNSCHWELLE_SEKUNDEN = 120;

/**
 * Ab welcher Abweichung **gemessen und protokolliert** wird — ohne den Prüfer zu behelligen.
 *
 * Dreissig Sekunden, und zwar gegen die **Serverzeit** (`driftSeconds` aus der Anmeldung).
 * Entscheidung des Nutzers vom 2026-09-16 auf Empfehlung dieser Seite: Der Versatz zum Server
 * ist das Mass, das für `creationDate` zählt — danach ordnet Relay die Protokollzeilen. Eine
 * **Meldung** mitten in der Station ist davon getrennt und bleibt bei 120 s: sie ist nur
 * gerechtfertigt, wenn wirklich etwas verstellt ist, und nicht, wenn eine Uhr driftet.
 *
 * ⚠️ **Die 30 s sind auch Relay' eigene Festlegung** (2026-09-06) — dort gebaut ist sie nicht;
 * der Server misst den Versatz und schreibt ihn nach `SecurityLog.clockDrift`, lehnt aber
 * niemanden ab (Rückmeldung der Relay-Sitzung, 2026-09-16).
 */
export const MESSSCHWELLE_SEKUNDEN = 30;

export interface Versatzurteil {
  /** Gehört ins Dateilog — und damit in die Spur, die ein Prüfungsleiter später zieht. */
  melden: boolean;
  /** Gehört dem Prüfer vor die Augen. */
  warnen: boolean;
}

/**
 * Was ein gemessener Versatz nach sich zieht. Rein, damit die zwei Schwellen an **einer**
 * Stelle stehen und prüfbar sind.
 */
export function beurteileVersatz(sekunden: number): Versatzurteil {
  if (!Number.isFinite(sekunden)) return { melden: false, warnen: false };
  const abs = Math.abs(sekunden);
  return { melden: abs >= MESSSCHWELLE_SEKUNDEN, warnen: abs >= WARNSCHWELLE_SEKUNDEN };
}

/** „3 Minuten vor" bzw. „1 Stunde 12 Minuten nach". */
export function beschreibeAbweichung(
  sekunden: number,
  worte: { vor: string; nach: string; stunde: string; stunden: string; minute: string; minuten: string; sekunden: string },
): string {
  const richtung = sekunden > 0 ? worte.nach : worte.vor;
  const abs = Math.round(Math.abs(sekunden));
  const stunden = Math.floor(abs / 3600);
  const minuten = Math.floor((abs % 3600) / 60);

  const teile: string[] = [];
  if (stunden > 0) teile.push(stunden + ' ' + (stunden === 1 ? worte.stunde : worte.stunden));
  if (minuten > 0) teile.push(minuten + ' ' + (minuten === 1 ? worte.minute : worte.minuten));
  // Unter einer Minute bleibt sonst nichts stehen; „0 Minuten" wäre keine Aussage.
  if (teile.length === 0) teile.push(abs + ' ' + worte.sekunden);

  return teile.join(' ') + ' ' + richtung;
}

/**
 * Was zuletzt über die Uhr bekannt ist.
 *
 * Damit jede Beglaubigung sagen kann, **woher ihre Zeit stammt**: nur aus der Systemuhr, oder
 * aus einer Systemuhr, die kurz zuvor gegen die Netzzeit geprüft wurde. Das ist der
 * Unterschied zwischen „das Gerät behauptet 10:05" und „das Gerät behauptet 10:05 und lag
 * vor drei Minuten 0,4 Sekunden neben der Netzzeit".
 *
 * Ein Modulzustand und kein Store: `platform/` darf nichts aus `store/` holen, und die
 * Ablage soll sich ohne Oberfläche prüfen lassen.
 *
 * ⚠️ **Beim Signieren wird nicht neu gefragt.** Ein NTP-Aufruf je Datei kostete bis zu
 * zweieinhalb Sekunden und bräuchte Netz — bei einem Bogen mit fünf Kacheln wäre das
 * unzumutbar. Stattdessen steht im Manifest, **wann** zuletzt geprüft wurde; wie viel eine
 * Prüfung von vor drei Stunden noch wert ist, entscheidet der Auswertende.
 */
export interface Zeitpruefung {
  /** Abweichung der Geräteuhr von der Netzzeit in Sekunden, positiv = Gerät geht nach. */
  offsetSeconds: number;
  /** Wann geprüft wurde, Relay-Zeitform. */
  checkedAt: string;
  /** Gegen wen geprüft wurde. */
  server: string;
}

let letzte: Zeitpruefung | null = null;
/** Zeitpunkt der letzten Messung, monoton (`performance.now`) statt aus der Uhr. */
let letzteMessungBei: number | null = null;

/**
 * Wie lange eine Messung „frisch genug" ist.
 *
 * Zehn Minuten. Die Prüfung läuft beim Start und bei jeder Rückkehr in den Vordergrund —
 * und ein Prüfer wechselt an einem Vormittag oft genug zwischen Apps, dass daraus ohne
 * Sperre Dutzende Anfragen an einen fremden Server würden. Eine Uhr driftet in zehn Minuten
 * nicht messbar; wird sie verstellt, fällt es bei der nächsten Messung auf.
 */
export const MINDESTABSTAND_MS = 10 * 60 * 1000;

/**
 * Ob sich eine neue Messung lohnt.
 *
 * ⚠️ Gemessen wird gegen `performance.now()` und **nicht** gegen die Systemuhr: die ist ja
 * gerade das, was in Zweifel steht. Wer sie um eine Stunde zurückstellt, dürfte sonst die
 * Sperre umgehen oder — schlimmer — sie versehentlich für immer aktivieren.
 */
export function brauchtNeueMessung(jetzt = performance.now()): boolean {
  if (letzteMessungBei === null) return true;
  return jetzt - letzteMessungBei >= MINDESTABSTAND_MS;
}

export const merkeZeitpruefung = (p: Zeitpruefung, jetzt = performance.now()) => {
  letzte = p;
  letzteMessungBei = jetzt;
};

export const letzteZeitpruefung = (): Zeitpruefung | null => letzte;


/* ------------------------------------------------- Der Versatz, den der Server meldet */

/**
 * Die Oberfläche meldet an, wie sie eine Warnung anzeigt.
 *
 * ⚠️ Derselbe Rückrufweg wie bei der Fotos-Kopie (`platform/fotos.ts`): `platform/` darf nichts
 * aus `store/` oder den Blättern holen. Ohne angemeldete Stelle wird nur protokolliert — das
 * ist im Browser und in Tests genau richtig.
 */
export type Versatzmelder = (sekunden: number) => void;

let versatzmelder: Versatzmelder | null = null;
export const setVersatzmelder = (fn: Versatzmelder | null) => {
  versatzmelder = fn;
};

/** Der zuletzt vom Server gemeldete Versatz, für die Beglaubigung und zum Nachsehen. */
let letzterServerversatz: number | null = null;
export const serverversatz = (): number | null => letzterServerversatz;

/**
 * Den Versatz verarbeiten, den Relay bei der Anmeldung mitgibt (`driftSeconds`).
 *
 * ⚠️ **Zwei Schwellen, zwei Zwecke.** Ab 30 s steht es im Dateilog, ab 120 s sieht es der
 * Prüfer. Dazwischen liegt der Bereich, in dem eine Uhr einfach driftet: das gehört in die
 * Spur, aber nicht in eine Meldung mitten in der Station.
 *
 * ⚠️ **Wirft nie.** Ein Versatz ist eine Auskunft über die Uhr, kein Grund, eine Anmeldung
 * scheitern zu lassen.
 */
export async function verarbeiteServerversatz(
  sekunden: number,
  log: (zeile: string) => Promise<void>,
): Promise<Versatzurteil> {
  const urteil = beurteileVersatz(sekunden);
  if (!Number.isFinite(sekunden)) return urteil;
  letzterServerversatz = sekunden;

  if (urteil.melden) {
    await log(
      'Zeitversatz zur Serverzeit: ' + Math.round(sekunden) + ' s' +
        (urteil.warnen ? ' — der Prüfer wird gewarnt.' : ' — protokolliert, keine Meldung an den Prüfer.'),
    ).catch(() => undefined);
  }
  if (urteil.warnen && versatzmelder) versatzmelder(sekunden);
  return urteil;
}

/** Nur für Tests. */
export function vergissServerversatz(): void {
  letzterServerversatz = null;
  versatzmelder = null;
}
